import dayjs from '~/timeInit';

import { APP_EXCLUSIVE_START_HOUR, APP_EXCLUSIVE_TIMEZONE } from '~/consts/appExclusive';

import type {
  EligibleAppExclusiveOffer,
  EligibleAppExclusiveOffersResponse,
  ScheduleRecord,
  TakenAppExclusive,
} from '~/services/OffersService';

export type AppExclusiveStatus = 'scheduled' | 'live' | 'ended';

export interface CalendarDay {
  date: string;
  dayOfMonth: number;
  inMonth: boolean;
  isWeekend: boolean;
}

const DATE_FORMAT = 'YYYY-MM-DD';
const MONTH_FORMAT = 'YYYY-MM';
const DAYS_PER_WEEK = 7;

// Monday-first weekday index (0 = Monday ... 6 = Sunday).
function mondayIndex(date: dayjs.Dayjs): number {
  return (date.day() + 6) % DAYS_PER_WEEK;
}

function parseMonth(month: string): dayjs.Dayjs {
  return dayjs.utc(`${month}-01`, DATE_FORMAT);
}

export function currentMonth(now: dayjs.Dayjs = dayjs()): string {
  return now.tz(APP_EXCLUSIVE_TIMEZONE).format(MONTH_FORMAT);
}

export function todayDate(now: dayjs.Dayjs = dayjs()): string {
  return now.tz(APP_EXCLUSIVE_TIMEZONE).format(DATE_FORMAT);
}

/** Sydney day after today: today's 6:00am slot has usually started, so tomorrow is the first useful pick. */
export function defaultAddDealDate(now: dayjs.Dayjs = dayjs()): string {
  return now.tz(APP_EXCLUSIVE_TIMEZONE).add(1, 'day').format(DATE_FORMAT);
}

export function shiftMonth(month: string, delta: number): string {
  return parseMonth(month).add(delta, 'month').format(MONTH_FORMAT);
}

export function formatMonthLabel(month: string): string {
  return parseMonth(month).format('MMMM YYYY');
}

/** Whole weeks (Monday to Sunday) covering the month, including leading and trailing days. */
export function buildCalendarDays(month: string): Array<CalendarDay> {
  const first = parseMonth(month);
  const gridStart = first.subtract(mondayIndex(first), 'day');
  const totalDays = Math.ceil((mondayIndex(first) + first.daysInMonth()) / DAYS_PER_WEEK) * DAYS_PER_WEEK;

  return Array.from({ length: totalDays }, (_, index) => {
    const date = gridStart.add(index, 'day');
    return {
      date: date.format(DATE_FORMAT),
      dayOfMonth: date.date(),
      inMonth: date.format(MONTH_FORMAT) === month,
      isWeekend: mondayIndex(date) >= 5,
    };
  });
}

/**
 * Range for the schedules search. With `overlaps` svc-offer returns every row touching the range,
 * including long rows that start before or end after the grid; without it (older svc-offer) only rows
 * fully inside are returned, so the range is padded by a day either side to keep edge slots.
 */
export function getCalendarQueryRange(days: Array<CalendarDay>): { start: string; end: string } {
  const first = dayjs.tz(days[0].date, APP_EXCLUSIVE_TIMEZONE).subtract(1, 'day');
  const last = dayjs.tz(days[days.length - 1].date, APP_EXCLUSIVE_TIMEZONE).add(2, 'day');
  return { start: first.toISOString(), end: last.toISOString() };
}

export function getScheduleStatus(
  schedule: Pick<ScheduleRecord, 'start' | 'end'>,
  now: dayjs.Dayjs = dayjs(),
): AppExclusiveStatus {
  if (now.isBefore(dayjs(schedule.start))) {
    return 'scheduled';
  }
  if (now.isBefore(dayjs(schedule.end))) {
    return 'live';
  }
  return 'ended';
}

const START_TIME = `T${String(APP_EXCLUSIVE_START_HOUR).padStart(2, '0')}:00:00`;

/** 6:00am Sydney time on a calendar day (YYYY-MM-DD). */
function sydneyStartOfSlot(date: string): dayjs.Dayjs {
  return dayjs.tz(`${date}${START_TIME}`, APP_EXCLUSIVE_TIMEZONE);
}

/**
 * The Sydney slot starting at 6:00am on a calendar day and running `days` whole days, so a slot across a
 * daylight saving change still ends at 6:00am (23, 25, 47 or 49 hours). Both ends are parsed as Sydney
 * wall-clock times: dayjs's `.add(n, 'day')` and `.hour()` on a `.tz()` value only follow Sydney's
 * daylight saving when the browser itself is on Sydney time.
 */
export function getDaySlot(date: string, days = 1): { start: dayjs.Dayjs; end: dayjs.Dayjs } {
  const endDate = dayjs.utc(date, DATE_FORMAT).add(days, 'day').format(DATE_FORMAT);
  return { start: sydneyStartOfSlot(date), end: sydneyStartOfSlot(endDate) };
}

/**
 * Places each schedule on every visible day whose slot it overlaps, the same rule svc-offer uses to
 * count deals on a day. A 48 hour deal shows on both of its days.
 */
export function groupSchedulesByDate(
  schedules: Array<ScheduleRecord>,
  days: Array<Pick<CalendarDay, 'date'>>,
): Map<string, Array<ScheduleRecord>> {
  const sorted = [...schedules].sort((a, b) => a.start.localeCompare(b.start) || a.id - b.id);
  const byDate = new Map<string, Array<ScheduleRecord>>();
  days.forEach(({ date }) => {
    const slot = getDaySlot(date);
    const overlapping = sorted.filter(
      (schedule) => dayjs(schedule.start).isBefore(slot.end) && dayjs(schedule.end).isAfter(slot.start),
    );
    if (overlapping.length) {
      byDate.set(date, overlapping);
    }
  });
  return byDate;
}

const HOURS_PER_DAY = 24;

/**
 * Whole days a schedule runs (1 = 24 hours, 2 = 48 hours). Rounded, so a slot across a daylight saving
 * change (23 or 25 hours) and a legacy row ending at 5:59:59am still count as whole days.
 */
export function getScheduleLengthDays(schedule: Pick<ScheduleRecord, 'start' | 'end'>): number {
  const hours = dayjs(schedule.end).diff(dayjs(schedule.start), 'hour', true);
  return Math.max(1, Math.round(hours / HOURS_PER_DAY));
}

/** A schedule spanning more than one daily slot. */
export function isMultiDaySchedule(schedule: Pick<ScheduleRecord, 'start' | 'end'>): boolean {
  return getScheduleLengthDays(schedule) > 1;
}

/** Short label for a slot length, e.g. "24h" or "48h". */
export function formatLengthLabel(days: number): string {
  return `${days * HOURS_PER_DAY}h`;
}

/** Sydney calendar days (YYYY-MM-DD) a 6:00am-aligned slot covers, one per day. */
export function getSlotDates(slot: { start: string; end: string }): Array<string> {
  const dates: Array<string> = [];
  const end = dayjs(slot.end);
  for (let day = dayjs(slot.start).tz(APP_EXCLUSIVE_TIMEZONE); day.isBefore(end); day = day.add(1, 'day')) {
    dates.push(day.format(DATE_FORMAT));
  }
  return dates;
}

export interface SlotDayDeals {
  date: string;
  deals: Array<TakenAppExclusive>;
}

/** A slot about to be written (e.g. the other half of a swap), counted as a deal on each day it covers. */
export interface PlannedSlot {
  start: string;
  end: string;
}

function overlapsDay(window: PlannedSlot, date: string): boolean {
  const day = getDaySlot(date);
  return dayjs(window.start).isBefore(day.end) && dayjs(window.end).isAfter(day.start);
}

/**
 * Most deals one region can hold on the same day, as svc-offer reports it. Older svc-offer has no
 * `maxPerDay` and allowed one deal per day.
 */
export function getMaxPerDay(eligible: Pick<EligibleAppExclusiveOffersResponse, 'maxPerDay'>): number {
  return eligible.maxPerDay ?? 1;
}

/**
 * The deals already on each Sydney day of the slot, without the rows in `ignoreScheduleIds` (rows about
 * to move or be removed). Older svc-offer has no `takenBy`, only the single `dayTakenBy`.
 */
export function getSlotDayDeals(
  eligible: Pick<EligibleAppExclusiveOffersResponse, 'slot' | 'takenBy' | 'dayTakenBy'>,
  ignoreScheduleIds: Array<number> = [],
): Array<SlotDayDeals> {
  const taken = (eligible.takenBy ?? (eligible.dayTakenBy ? [eligible.dayTakenBy] : [])).filter(
    (deal) => !ignoreScheduleIds.includes(deal.scheduleId),
  );
  return getSlotDates(eligible.slot).map((date) => ({
    date,
    deals: taken.filter((deal) => overlapsDay(deal, date)),
  }));
}

/**
 * Days of the slot that already hold the daily limit of deals, ignoring rows in `ignoreScheduleIds` and
 * counting each of `plannedSlots` on the days it covers.
 *
 * svc-offer's `fullDates` is the source of truth for a day, except where one of its rows is ignored (only
 * the frontend knows that row is about to move); those days, and every day on older svc-offer without
 * `fullDates`, are counted here from `takenBy`.
 */
export function getFullSlotDays(
  eligible: Pick<EligibleAppExclusiveOffersResponse, 'slot' | 'takenBy' | 'dayTakenBy' | 'maxPerDay' | 'fullDates'>,
  ignoreScheduleIds: Array<number> = [],
  plannedSlots: Array<PlannedSlot> = [],
): Array<SlotDayDeals> {
  const maxPerDay = getMaxPerDay(eligible);
  const allDeals = getSlotDayDeals(eligible);
  return getSlotDayDeals(eligible, ignoreScheduleIds).filter((day, index) => {
    const hasIgnoredRow = allDeals[index].deals.length !== day.deals.length;
    const planned = plannedSlots.filter((slot) => overlapsDay(slot, day.date)).length;
    const fullPerSvcOffer = !hasIgnoredRow && !!eligible.fullDates?.includes(day.date);
    return fullPerSvcOffer || day.deals.length + planned >= maxPerDay;
  });
}

/**
 * Why svc-offer's slot can't be used for a deal of `days` days, or `undefined` when it can. Older
 * svc-offer ignores `days` and always returns a 24 hour slot; creating from it would quietly shorten a
 * 48 hour deal.
 */
export function getSlotLengthMismatch(
  eligible: Pick<EligibleAppExclusiveOffersResponse, 'slot'>,
  days: number,
): string | undefined {
  const slotDays = getScheduleLengthDays(eligible.slot);
  if (slotDays === days) {
    return undefined;
  }
  return `svc-offer returned a ${formatLengthLabel(slotDays)} slot instead of ${formatLengthLabel(
    days,
  )}; it may not support ${formatLengthLabel(days)} deals yet`;
}

export function describeFullDay({ date, deals }: SlotDayDeals): string {
  if (!deals.length) {
    // Full only once the other deal being moved lands here (a swap).
    return `${formatDayLabel(date)} would go over the daily limit with the other deal in the swap`;
  }
  return `${formatDayLabel(date)} already has ${deals.length} deal${deals.length === 1 ? '' : 's'} (offer${
    deals.length === 1 ? '' : 's'
  } ${deals.map((deal) => deal.offerId).join(', ')})`;
}

export function formatSlotTime(iso: string): string {
  return dayjs(iso).tz(APP_EXCLUSIVE_TIMEZONE).format('D MMM, h:mma');
}

export function formatSlotTimeOfDay(iso: string): string {
  return dayjs(iso).tz(APP_EXCLUSIVE_TIMEZONE).format('h:mma');
}

// Defensive: other offer shapes (e.g. App.ContentOffer) model location as a plain string.
type OfferLocation = App.Offer['location'] | string;

/**
 * Short location label for an event card. Accommodation offers carry a structured location
 * (`heading`/`subheading`/`description`); tolerate a plain string too.
 */
export function getOfferLocationLabel(location: OfferLocation | undefined): string | undefined {
  if (!location) {
    return undefined;
  }
  if (typeof location === 'string') {
    return location || undefined;
  }
  return [location.heading, location.subheading].filter(Boolean).join(', ') || undefined;
}

/**
 * Why an eligible offer can't take the slot, or `undefined` when it can. svc-offer allows one
 * `app_exclusive` row per offer and region, so an offer that already has one (upcoming or ended)
 * can't get a second; a taken or ended day blocks every offer and is reported once for the day.
 */
export function getOfferBlockReason(offer: EligibleAppExclusiveOffer, now: dayjs.Dayjs = dayjs()): string | undefined {
  if (offer.canSchedule) {
    return undefined;
  }
  const existing = offer.existingAppExclusive;
  if (!existing) {
    return 'This slot is full or has started';
  }
  const existingDay = dayjs(existing.start).tz(APP_EXCLUSIVE_TIMEZONE).format('D MMM YYYY');
  return getScheduleStatus(existing, now) === 'ended'
    ? `Already ran as App Only Flash on ${existingDay}`
    : `Already booked for ${existingDay}`;
}

/** The deal stops selling when the first of its availability and online purchase windows closes. */
export function getOnSaleEnd(offer: Pick<EligibleAppExclusiveOffer, 'availability' | 'onlinePurchase'>): string {
  const availabilityEnd = dayjs(offer.availability.end);
  const onlinePurchaseEnd = dayjs(offer.onlinePurchase.end);
  return (availabilityEnd.isBefore(onlinePurchaseEnd) ? availabilityEnd : onlinePurchaseEnd).toISOString();
}

/** Sydney calendar day (YYYY-MM-DD) whose 6:00am slot a schedule starts in. */
export function getScheduleDate(schedule: Pick<ScheduleRecord, 'start'>): string {
  return dayjs(schedule.start).tz(APP_EXCLUSIVE_TIMEZONE).format(DATE_FORMAT);
}

export function formatDayLabel(date: string): string {
  return dayjs.utc(date, DATE_FORMAT).format('ddd D MMM');
}

/**
 * Why `offerId` can't take the slot in `eligible`, or `undefined` when it can. Rows in `ignoreScheduleIds`
 * are about to move or be removed (a move or a swap), so they neither count towards a day's limit nor
 * as the offer's existing App Only Flash row. `plannedSlots` are rows about to be written elsewhere in
 * the same change (the other half of a swap), so they count on the days they cover.
 */
export function getSlotBlockReason(
  eligible: EligibleAppExclusiveOffersResponse,
  offerId: string,
  ignoreScheduleIds: Array<number> = [],
  now: dayjs.Dayjs = dayjs(),
  plannedSlots: Array<PlannedSlot> = [],
): string | undefined {
  if (!now.isBefore(dayjs(eligible.slot.start))) {
    return 'This day has already started';
  }
  const [fullDay] = getFullSlotDays(eligible, ignoreScheduleIds, plannedSlots);
  if (fullDay) {
    return describeFullDay(fullDay);
  }
  const offer = eligible.offers.find((item) => item.offerId === offerId);
  if (!offer && eligible.total > eligible.offers.length) {
    // The list was cut off by `limit`, so a missing offer may still be eligible.
    return `Could not confirm the offer is on sale in ${eligible.region}: too many offers match its name`;
  }
  if (!offer) {
    return `Not on sale in ${eligible.region} until ${formatSlotTime(eligible.slot.onSaleUntil)}, or still on the homepage`;
  }
  const existing = offer.existingAppExclusive;
  if (existing && !ignoreScheduleIds.includes(existing.scheduleId)) {
    return getOfferBlockReason({ ...offer, canSchedule: false }, now);
  }
  return undefined;
}

/**
 * Tomorrow and the day after with nothing scheduled. Tomorrow's deal sits on the homepage all day today,
 * so an empty day here needs filling soon.
 */
export function getUpcomingEmptyDays(schedules: Array<ScheduleRecord>, today: string): Array<string> {
  const upcoming = [1, 2].map((offset) => ({
    date: dayjs.utc(today, DATE_FORMAT).add(offset, 'day').format(DATE_FORMAT),
  }));
  const byDate = groupSchedulesByDate(schedules, upcoming);
  return upcoming.map(({ date }) => date).filter((date) => !byDate.has(date));
}

/** Query range covering the slots of the two Sydney days after `today` (YYYY-MM-DD). */
export function getUpcomingQueryRange(today: string): { start: string; end: string } {
  const tomorrow = dayjs.utc(today, DATE_FORMAT).add(1, 'day').format(DATE_FORMAT);
  const start = getDaySlot(tomorrow).start;
  return { start: start.toISOString(), end: start.add(2, 'day').toISOString() };
}
